// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  createConfiguration,
  DefaultApi,
  ServerConfiguration,
  Middleware,
  RequestContext,
  ResponseContext,
} from '@digitalasset/json-api-ts-client';
import { useConfig } from '@hooks/other/useConfig';
import React, { useMemo } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { WSSupport, WSSupportWithBackOffSwitch } from '../utils/WSSupport';
import { ensureNoTrailingSlash } from '../utils/urls';
import { JsonApiContext } from './JsonApiContext';
import { useLocalStorage } from './LocalStorageContext';
import { useUserState } from './UserContext';

class ApiMiddleware
  extends BaseApiMiddleware<RequestContext, ResponseContext>
  implements Middleware {}

interface JsonApiProps {
  jsonApiUrl: string;
}

export const JsonApiProvider = ({
  jsonApiUrl,
  children,
}: React.PropsWithChildren<JsonApiProps>) => {
  const { loggedInUser } = useUserState();
  const config = useConfig();
  const { localStorageState } = useLocalStorage();
  const { webSocketEnabled } = localStorageState;
  const jsonApiClient = useMemo(() => {
    if (loggedInUser) {
      const configuration = createConfiguration({
        baseServer: new ServerConfiguration(ensureNoTrailingSlash(jsonApiUrl), {}),
        promiseMiddleware: [new ApiMiddleware(loggedInUser.userAccessToken)],
      });
      const apiUrl = new URL(ensureNoTrailingSlash(jsonApiUrl));
      const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiUrl.host}${apiUrl.pathname}`;
      const wsSupport: WSSupport = new WSSupportWithBackOffSwitch(
        () => {
          const token = loggedInUser.userAccessToken;
          return [`jwt.token.${token}`, 'daml.ws.auth'];
        },
        wsUrl,
        config.app.query_limit,
      );
      wsSupport.setEnabled(!!webSocketEnabled);
      const setWsEnabled = (enable: boolean) => {
        wsSupport.setEnabled(enable);
      };
      return { jsonApi: new DefaultApi(configuration), wsSupport, setWsEnabled };
    }
    return undefined;
  }, [jsonApiUrl, loggedInUser, config.app.query_limit, webSocketEnabled]);

  return <JsonApiContext.Provider value={jsonApiClient}>{children}</JsonApiContext.Provider>;
};
